import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Stack,
  IconButton,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TuneIcon from '@mui/icons-material/Tune';
import { leaveApi } from './leaveApi';
import { TABLE_PAPER_SX, HEAD_CELL_SX, BODY_CELL_SX, TABLE_ROW_SX } from '../common/tableStyles';
import { formatDate } from '../../utils/dateFormat';

const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';

export default function EscalationsAndHolidaysPanel() {
  const [subTab, setSubTab] = useState(0);

  // General feedback
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // 1. Holidays state
  const [holidays, setHolidays] = useState([]);
  const [loadingHolidays, setLoadingHolidays] = useState(false);
  const [holidayDialogOpen, setHolidayDialogOpen] = useState(false);
  const [holidayStateFilter, setHolidayStateFilter] = useState('');
  const [holidayCategoryFilter, setHolidayCategoryFilter] = useState('');
  const [holidayForm, setHolidayForm] = useState({
    name: '',
    date: '',
    category: 'NATIONAL',
    state: '',
    description: '',
    is_half_day: false,
    session: 'FULL_DAY',
  });

  // 2. Restricted Holidays (RH)
  const [restrictedHolidays, setRestrictedHolidays] = useState([]);
  const [rhSelections, setRhSelections] = useState([]);
  const [loadingRH, setLoadingRH] = useState(false);
  const [rhDialogOpen, setRhDialogOpen] = useState(false);
  const [selectedRhHolidayId, setSelectedRhHolidayId] = useState('');
  const [rhNotes, setRhNotes] = useState('');

  // 3. Monthly Accrual
  const [accrualPeriod, setAccrualPeriod] = useState(new Date().toISOString().slice(0, 7) + '-01');
  const [accrualLoading, setAccrualLoading] = useState(false);
  const [accrualResult, setAccrualResult] = useState(null);

  // 4. Sandwich Engine
  const [sandwichPreviewForm, setSandwichPreviewForm] = useState({
    start_date: '',
    end_date: '',
    leave_type: 'CL',
    has_medical_certificate: false,
  });
  const [sandwichPreviewResult, setSandwichPreviewResult] = useState(null);
  const [calculatingSandwich, setCalculatingSandwich] = useState(false);
  const [sandwichProposals, setSandwichProposals] = useState([]);
  const [loadingProposals, setLoadingProposals] = useState(false);

  // 5. Unexcused Absences
  const [absenceTargetDate, setAbsenceTargetDate] = useState(new Date().toISOString().slice(0, 10));
  const [absenceCandidates, setAbsenceCandidates] = useState([]);
  const [loadingAbsences, setLoadingAbsences] = useState(false);
  const [scanningAbsences, setScanningAbsences] = useState(false);
  const [absenceReviewDialog, setAbsenceReviewDialog] = useState(false);
  const [selectedAbsence, setSelectedAbsence] = useState(null);
  const [absenceDecision, setAbsenceDecision] = useState('EXCUSED');
  const [absenceNotes, setAbsenceNotes] = useState('');

  // 6. Escalations state
  const [escalations, setEscalations] = useState([]);
  const [loadingEscalations, setLoadingEscalations] = useState(false);
  const [scanning, setScanning] = useState(false);

  // Data fetchers
  const fetchHolidays = useCallback(async () => {
    setLoadingHolidays(true);
    try {
      let query = '';
      const params = [];
      if (holidayStateFilter) params.push(`state=${encodeURIComponent(holidayStateFilter)}`);
      if (holidayCategoryFilter) params.push(`category=${encodeURIComponent(holidayCategoryFilter)}`);
      if (params.length > 0) query = params.join('&');

      const data = await leaveApi.getHolidays(query);
      setHolidays(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Failed to load holidays.');
    } finally {
      setLoadingHolidays(false);
    }
  }, [holidayStateFilter, holidayCategoryFilter]);

  const fetchRestrictedData = useCallback(async () => {
    setLoadingRH(true);
    try {
      const [rhs, selections] = await Promise.all([
        leaveApi.getRestrictedHolidays(),
        leaveApi.getRestrictedSelections(),
      ]);
      setRestrictedHolidays(Array.isArray(rhs) ? rhs : rhs.results || []);
      setRhSelections(Array.isArray(selections) ? selections : selections.results || []);
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Failed to load restricted holiday data.');
    } finally {
      setLoadingRH(false);
    }
  }, []);

  const fetchSandwichProposals = useCallback(async () => {
    setLoadingProposals(true);
    try {
      const data = await leaveApi.getSandwichProposals();
      setSandwichProposals(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Failed to load sandwich proposals.');
    } finally {
      setLoadingProposals(false);
    }
  }, []);

  const fetchAbsenceCandidates = useCallback(async () => {
    setLoadingAbsences(true);
    try {
      const data = await leaveApi.getAbsenceCandidates();
      setAbsenceCandidates(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Failed to load absence candidates.');
    } finally {
      setLoadingAbsences(false);
    }
  }, []);

  const fetchEscalations = useCallback(async () => {
    setLoadingEscalations(true);
    try {
      const data = await leaveApi.getEscalations();
      setEscalations(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Failed to load escalated leaves.');
    } finally {
      setLoadingEscalations(false);
    }
  }, []);

  useEffect(() => {
    if (subTab === 0) fetchHolidays();
    if (subTab === 1) fetchRestrictedData();
    if (subTab === 3) fetchSandwichProposals();
    if (subTab === 4) fetchAbsenceCandidates();
    if (subTab === 5) fetchEscalations();
  }, [subTab, fetchHolidays, fetchRestrictedData, fetchSandwichProposals, fetchAbsenceCandidates, fetchEscalations]);

  // Holiday handlers
  const handleCreateHoliday = async () => {
    if (!holidayForm.name || !holidayForm.date) {
      setErrorMsg('Holiday name and date are required.');
      return;
    }
    try {
      await leaveApi.createHoliday(holidayForm);
      setSuccessMsg('Holiday created successfully.');
      setHolidayDialogOpen(false);
      setHolidayForm({
        name: '',
        date: '',
        category: 'NATIONAL',
        state: '',
        description: '',
        is_half_day: false,
        session: 'FULL_DAY',
      });
      fetchHolidays();
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Failed to create holiday.');
    }
  };

  const handleDeleteHoliday = async (id) => {
    if (!window.confirm('Delete this declared holiday?')) return;
    try {
      await leaveApi.deleteHoliday(id);
      setSuccessMsg('Holiday deleted successfully.');
      fetchHolidays();
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Failed to delete holiday.');
    }
  };

  // Restricted Holiday handlers
  const handleSelectRH = async () => {
    if (!selectedRhHolidayId) {
      setErrorMsg('Please select a restricted holiday.');
      return;
    }
    try {
      await leaveApi.selectRestrictedHoliday({
        holiday: selectedRhHolidayId,
        notes: rhNotes,
      });
      setSuccessMsg('Restricted holiday selected successfully.');
      setRhDialogOpen(false);
      setSelectedRhHolidayId('');
      setRhNotes('');
      fetchRestrictedData();
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Failed to select restricted holiday.');
    }
  };

  const handleCancelRH = async (id) => {
    if (!window.confirm('Cancel this restricted holiday selection?')) return;
    try {
      await leaveApi.cancelRestrictedHoliday(id);
      setSuccessMsg('Restricted holiday selection cancelled.');
      fetchRestrictedData();
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Failed to cancel selection.');
    }
  };

  // Accrual handlers
  const handleRunAccrual = async (dryRun = true) => {
    setAccrualLoading(true);
    setErrorMsg('');
    try {
      const res = dryRun ? await leaveApi.previewAccrual(accrualPeriod) : await leaveApi.runAccrual(accrualPeriod);
      setAccrualResult(res);
      const evaluatedCount = res.eligible_employees_count ?? res.scanned_employees ?? 0;
      const creditedCount = res.credited_count ?? res.accruals_posted ?? 0;
      setSuccessMsg(
        dryRun
          ? `Dry run preview complete: ${evaluatedCount} employees evaluated.`
          : `Accrual execution complete: ${creditedCount} employees credited.`
      );
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Accrual operation failed.');
    } finally {
      setAccrualLoading(false);
    }
  };

  // Sandwich Preview handlers
  const handlePreviewSandwich = async () => {
    if (!sandwichPreviewForm.start_date || !sandwichPreviewForm.end_date) {
      setErrorMsg('Start date and end date are required.');
      return;
    }
    setCalculatingSandwich(true);
    setErrorMsg('');
    try {
      const res = await leaveApi.previewSandwich(sandwichPreviewForm);
      setSandwichPreviewResult(res);
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Failed to preview sandwich rule.');
    } finally {
      setCalculatingSandwich(false);
    }
  };

  const handleApplySandwichProposal = async (id) => {
    try {
      await leaveApi.applySandwichProposal(id);
      setSuccessMsg('Sandwich proposal applied and ledger updated.');
      fetchSandwichProposals();
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Failed to apply sandwich proposal.');
    }
  };

  // Absence Review handlers
  const handleScanAbsences = async () => {
    setScanningAbsences(true);
    setErrorMsg('');
    try {
      const res = await leaveApi.scanAbsenceCandidates(absenceTargetDate);
      setSuccessMsg(`Absence scan complete: ${res.scanned || 0} scanned, ${res.candidates_created || 0} candidates found.`);
      fetchAbsenceCandidates();
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Failed to execute absence scan.');
    } finally {
      setScanningAbsences(false);
    }
  };

  const handleRecordAbsenceDecision = async () => {
    if (!selectedAbsence) return;
    try {
      await leaveApi.decideAbsenceCandidate(selectedAbsence.id, absenceDecision, absenceNotes);
      setSuccessMsg('Absence review decision recorded.');
      setAbsenceReviewDialog(false);
      setSelectedAbsence(null);
      setAbsenceNotes('');
      fetchAbsenceCandidates();
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Failed to record absence decision.');
    }
  };

  // Stale Scan handler
  const handleTriggerStaleScan = async () => {
    setScanning(true);
    setErrorMsg('');
    try {
      const result = await leaveApi.triggerStaleEvaluation();
      setSuccessMsg(
        `Stale evaluation scan complete: ${result.scanned} scanned, ${result.auto_approved} auto-approved, ${result.escalated} escalated.`
      );
      fetchEscalations();
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Failed to execute stale evaluation scan.');
    } finally {
      setScanning(false);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      {/* Sub Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: '#e2e8f0', mb: 3 }}>
        <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<EventAvailableIcon fontSize="small" />} iconPosition="start" label="Company & Multi-State Holidays" />
          <Tab icon={<CalendarMonthIcon fontSize="small" />} iconPosition="start" label="Restricted Holidays (RH)" />
          <Tab icon={<AssessmentIcon fontSize="small" />} iconPosition="start" label="Monthly Accrual Engine" />
          <Tab icon={<TuneIcon fontSize="small" />} iconPosition="start" label="Sandwich Leave Engine" />
          <Tab icon={<WarningAmberIcon fontSize="small" />} iconPosition="start" label="Unexcused Absence Reviews" />
          <Tab icon={<WarningAmberIcon fontSize="small" />} iconPosition="start" label="Stale Escalations (Batch 2)" />
        </Tabs>
      </Box>

      {errorMsg && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setErrorMsg('')}>
          {errorMsg}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}

      {/* SUB-TAB 0: Gazetted & Multi-State Holidays */}
      {subTab === 0 && (
        <>
          <Paper elevation={0} sx={{ ...CARD_SX, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827' }}>
                  Gazetted & Company Holidays
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  Universal national holidays, state-level festivals, and branch-specific holidays (TASK-LEV-011).
                </Typography>
              </Box>
              <Stack direction="row" spacing={2} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Filter State</InputLabel>
                  <Select value={holidayStateFilter} label="Filter State" onChange={(e) => setHolidayStateFilter(e.target.value)}>
                    <MenuItem value="">All States</MenuItem>
                    <MenuItem value="KA">Karnataka (KA)</MenuItem>
                    <MenuItem value="MH">Maharashtra (MH)</MenuItem>
                    <MenuItem value="DL">Delhi (DL)</MenuItem>
                    <MenuItem value="TN">Tamil Nadu (TN)</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Filter Category</InputLabel>
                  <Select value={holidayCategoryFilter} label="Filter Category" onChange={(e) => setHolidayCategoryFilter(e.target.value)}>
                    <MenuItem value="">All Categories</MenuItem>
                    <MenuItem value="NATIONAL">National</MenuItem>
                    <MenuItem value="STATE">State</MenuItem>
                    <MenuItem value="COMPANY">Company</MenuItem>
                    <MenuItem value="BRANCH">Branch</MenuItem>
                    <MenuItem value="RESTRICTED">Restricted</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setHolidayDialogOpen(true)}
                  sx={{ bgcolor: ACCENT, fontWeight: 700, borderRadius: '12px', textTransform: 'none', '&:hover': { bgcolor: '#2557d6' } }}
                >
                  Add Holiday
                </Button>
              </Stack>
            </Box>
          </Paper>

          <TableContainer component={Paper} sx={{ ...TABLE_PAPER_SX, borderRadius: '22px', border: '1px solid #eef0f4' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={HEAD_CELL_SX}>HOLIDAY NAME</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>DATE</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>CATEGORY</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>STATE / SCOPE</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>DURATION</TableCell>
                  <TableCell sx={{ ...HEAD_CELL_SX, textAlign: 'center' }}>ACTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingHolidays ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                ) : holidays.length > 0 ? (
                  holidays.map((h) => (
                    <TableRow key={h.id} sx={TABLE_ROW_SX}>
                      <TableCell sx={BODY_CELL_SX}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                          {h.name}
                        </Typography>
                        {h.description && (
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>
                            {h.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <Typography variant="body2" sx={{ color: '#374151' }}>
                          {formatDate(h.date)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <Chip
                          size="small"
                          label={h.category || 'NATIONAL'}
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            borderRadius: '8px',
                            bgcolor: h.category === 'STATE' ? '#eff6ff' : h.category === 'RESTRICTED' ? '#fdf2f8' : '#f0fdf4',
                            color: h.category === 'STATE' ? '#1d4ed8' : h.category === 'RESTRICTED' ? '#be185d' : '#15803d',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          {h.state ? `State: ${h.state}` : h.branch_name ? `Branch: ${h.branch_name}` : 'Universal (All)'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <Chip
                          size="small"
                          label={h.is_half_day ? `Half Day (${h.session})` : 'Full Day'}
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            borderRadius: '8px',
                            bgcolor: h.is_half_day ? '#fffbeb' : '#ecfdf5',
                            color: h.is_half_day ? '#d97706' : '#059669',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...BODY_CELL_SX, textAlign: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteHoliday(h.id)}
                          sx={{ color: '#dc2626', bgcolor: '#fef2f2', '&:hover': { bgcolor: '#fee2e2' } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#6b7280' }}>
                      No declared holidays configured.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Add Holiday Dialog */}
          <Dialog open={holidayDialogOpen} onClose={() => setHolidayDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
            <DialogTitle sx={{ fontWeight: 700 }}>Add Holiday (TASK-LEV-011)</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField
                  label="Holiday Name *"
                  fullWidth
                  size="small"
                  value={holidayForm.name}
                  onChange={(e) => setHolidayForm({ ...holidayForm, name: e.target.value })}
                />
                <TextField
                  label="Date *"
                  type="date"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={holidayForm.date}
                  onChange={(e) => setHolidayForm({ ...holidayForm, date: e.target.value })}
                />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={holidayForm.category}
                        label="Category"
                        onChange={(e) => setHolidayForm({ ...holidayForm, category: e.target.value })}
                      >
                        <MenuItem value="NATIONAL">Universal National</MenuItem>
                        <MenuItem value="STATE">State-Level Festival</MenuItem>
                        <MenuItem value="COMPANY">Company Specific</MenuItem>
                        <MenuItem value="BRANCH">Branch Specific</MenuItem>
                        <MenuItem value="RESTRICTED">Restricted Holiday (RH)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="State Code (e.g. KA, MH)"
                      fullWidth
                      size="small"
                      value={holidayForm.state}
                      onChange={(e) => setHolidayForm({ ...holidayForm, state: e.target.value })}
                      disabled={holidayForm.category !== 'STATE'}
                    />
                  </Grid>
                </Grid>
                <TextField
                  label="Description / Festival Notes"
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  value={holidayForm.description}
                  onChange={(e) => setHolidayForm({ ...holidayForm, description: e.target.value })}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={holidayForm.is_half_day}
                      onChange={(e) => setHolidayForm({ ...holidayForm, is_half_day: e.target.checked })}
                    />
                  }
                  label="Half Day Holiday"
                />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setHolidayDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateHoliday} variant="contained" sx={{ bgcolor: ACCENT, borderRadius: '10px' }}>
                Save Holiday
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {/* SUB-TAB 1: Restricted Holidays (RH) */}
      {subTab === 1 && (
        <>
          <Paper elevation={0} sx={{ ...CARD_SX, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827' }}>
                  Restricted Holidays (RH) & Annual Quota Selection
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  Optional festival days that eligible employees may elect within their annual quota (default 2 days per year).
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setRhDialogOpen(true)}
                sx={{ bgcolor: ACCENT, fontWeight: 700, borderRadius: '12px', textTransform: 'none' }}
              >
                Elect Restricted Holiday
              </Button>
            </Box>
          </Paper>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: '#1f2937' }}>
            My Restricted Holiday Selections
          </Typography>
          <TableContainer component={Paper} sx={{ ...TABLE_PAPER_SX, borderRadius: '22px', border: '1px solid #eef0f4', mb: 4 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={HEAD_CELL_SX}>HOLIDAY NAME</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>DATE</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>YEAR</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>STATUS</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>NOTES</TableCell>
                  <TableCell sx={{ ...HEAD_CELL_SX, textAlign: 'center' }}>ACTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingRH ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                ) : rhSelections.length > 0 ? (
                  rhSelections.map((sel) => (
                    <TableRow key={sel.id} sx={TABLE_ROW_SX}>
                      <TableCell sx={BODY_CELL_SX}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {sel.holiday_name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>{formatDate(sel.holiday_date)}</TableCell>
                      <TableCell sx={BODY_CELL_SX}>{sel.calendar_year}</TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <Chip
                          size="small"
                          label={sel.status}
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            bgcolor: sel.status === 'APPROVED' ? '#ecfdf5' : sel.status === 'PENDING' ? '#fffbeb' : '#fef2f2',
                            color: sel.status === 'APPROVED' ? '#059669' : sel.status === 'PENDING' ? '#d97706' : '#dc2626',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>{sel.notes || '-'}</TableCell>
                      <TableCell sx={{ ...BODY_CELL_SX, textAlign: 'center' }}>
                        {sel.status !== 'CANCELLED' && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleCancelRH(sel.id)}
                            sx={{ borderRadius: '8px', textTransform: 'none' }}
                          >
                            Cancel
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#6b7280' }}>
                      No restricted holidays elected yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Dialog open={rhDialogOpen} onClose={() => setRhDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
            <DialogTitle sx={{ fontWeight: 700 }}>Elect Restricted Holiday</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Available RH Festival</InputLabel>
                  <Select value={selectedRhHolidayId} label="Available RH Festival" onChange={(e) => setSelectedRhHolidayId(e.target.value)}>
                    {restrictedHolidays.map((rh) => (
                      <MenuItem key={rh.id} value={rh.id}>
                        {rh.name} ({formatDate(rh.date)}) {rh.state ? `[${rh.state}]` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Employee Notes"
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  value={rhNotes}
                  onChange={(e) => setRhNotes(e.target.value)}
                  placeholder="Reason / Festival observation"
                />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setRhDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSelectRH} variant="contained" sx={{ bgcolor: ACCENT, borderRadius: '10px' }}>
                Confirm Election
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {/* SUB-TAB 2: Monthly Accrual Engine */}
      {subTab === 2 && (
        <>
          <Paper elevation={0} sx={{ ...CARD_SX, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>
              Monthly Leave Accrual Engine (TASK-LEV-006)
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>
              Executes deterministic monthly leave grants with mid-month pro-ration, probation ceiling checks, and maximum balance cap pause.
            </Typography>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Accrual Period (1st of Month)"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={accrualPeriod}
                  onChange={(e) => setAccrualPeriod(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    disabled={accrualLoading}
                    onClick={() => handleRunAccrual(true)}
                    sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}
                  >
                    Dry-Run Preview
                  </Button>
                  <Button
                    variant="contained"
                    disabled={accrualLoading}
                    onClick={() => handleRunAccrual(false)}
                    sx={{ bgcolor: ACCENT, borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}
                  >
                    Execute Monthly Accrual
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          {accrualResult && (
            <Paper elevation={0} sx={{ ...CARD_SX, mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                Accrual Execution Summary ({(accrualResult.dry_run || accrualResult.status === 'DRY_RUN_COMPLETED') ? 'Dry Run Preview' : 'Committed to Ledger'})
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={3}>
                  <Paper sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '12px', textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>TOTAL ELIGIBLE</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>{accrualResult.eligible_employees_count ?? accrualResult.scanned_employees ?? 0}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={3}>
                  <Paper sx={{ p: 2, bgcolor: '#f0fdf4', borderRadius: '12px', textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: '#16a34a' }}>CREDITED</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#15803d' }}>{accrualResult.credited_count ?? accrualResult.accruals_posted ?? 0}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={3}>
                  <Paper sx={{ p: 2, bgcolor: '#fffbeb', borderRadius: '12px', textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: '#d97706' }}>SKIPPED (CAP/PROBATION)</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#b45309' }}>{accrualResult.skipped_count || 0}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={3}>
                  <Paper sx={{ p: 2, bgcolor: '#eff6ff', borderRadius: '12px', textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: '#2563eb' }}>TOTAL DAYS CREDITED</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#1d4ed8' }}>{accrualResult.total_days_credited ?? accrualResult.total_days_accrued ?? 0}</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          )}
        </>
      )}

      {/* SUB-TAB 3: Sandwich Leave Engine */}
      {subTab === 3 && (
        <>
          <Paper elevation={0} sx={{ ...CARD_SX, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>
              Indian Sandwich Leave Engine (TASK-LEV-007)
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>
              Calculates sandwich impact when leaves flank weekends or gazetted holidays. Statutorily exempts certified Sick Leaves, Maternity, Paternity, and Bereavement.
            </Typography>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Start Date *"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={sandwichPreviewForm.start_date}
                  onChange={(e) => setSandwichPreviewForm({ ...sandwichPreviewForm, start_date: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="End Date *"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={sandwichPreviewForm.end_date}
                  onChange={(e) => setSandwichPreviewForm({ ...sandwichPreviewForm, end_date: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sandwichPreviewForm.has_medical_certificate}
                      onChange={(e) => setSandwichPreviewForm({ ...sandwichPreviewForm, has_medical_certificate: e.target.checked })}
                    />
                  }
                  label="Verified Medical Cert"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button
                  fullWidth
                  variant="contained"
                  disabled={calculatingSandwich}
                  onClick={handlePreviewSandwich}
                  sx={{ bgcolor: ACCENT, borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}
                >
                  Preview Sandwich Days
                </Button>
              </Grid>
            </Grid>

            {sandwichPreviewResult && (
              <Box sx={{ mt: 3, p: 2, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Sandwich Calculation Result:
                </Typography>
                <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }}>
                  <Typography variant="body2">Requested Days: <strong>{sandwichPreviewResult.requested_days ?? sandwichPreviewResult.base_working_days ?? 0}</strong></Typography>
                  <Typography variant="body2">Sandwich Intervening Days: <strong>{sandwichPreviewResult.sandwich_days ?? sandwichPreviewResult.additional_sandwich_days ?? 0}</strong></Typography>
                  <Typography variant="body2">Total Debited: <strong>{sandwichPreviewResult.total_effective_days ?? sandwichPreviewResult.total_chargeable_days ?? 0}</strong></Typography>
                  <Typography variant="body2">
                    Exempted:{' '}
                    <strong style={{ color: sandwichPreviewResult.is_exempt ? '#16a34a' : '#64748b' }}>
                      {sandwichPreviewResult.is_exempt ? `Yes (${sandwichPreviewResult.exemption_reason || sandwichPreviewResult.explanation || 'Exempt'})` : 'No'}
                    </strong>
                  </Typography>
                </Stack>
              </Box>
            )}
          </Paper>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: '#1f2937' }}>
            Active Sandwich Proposals (Awaiting HR Review)
          </Typography>
          <TableContainer component={Paper} sx={{ ...TABLE_PAPER_SX, borderRadius: '22px', border: '1px solid #eef0f4' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={HEAD_CELL_SX}>ID</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>EMPLOYEE</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>SPAN</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>SANDWICH DAYS</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>STATUS</TableCell>
                  <TableCell sx={{ ...HEAD_CELL_SX, textAlign: 'center' }}>ACTION</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingProposals ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                ) : sandwichProposals.length > 0 ? (
                  sandwichProposals.map((prop) => (
                    <TableRow key={prop.id} sx={TABLE_ROW_SX}>
                      <TableCell sx={BODY_CELL_SX}>#{prop.id}</TableCell>
                      <TableCell sx={BODY_CELL_SX}>{prop.employee_name || prop.employee}</TableCell>
                      <TableCell sx={BODY_CELL_SX}>{formatDate(prop.start_date)} - {formatDate(prop.end_date)}</TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <Chip size="small" label={`+${prop.sandwich_days} days`} color="warning" />
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <Chip size="small" label={prop.status} />
                      </TableCell>
                      <TableCell sx={{ ...BODY_CELL_SX, textAlign: 'center' }}>
                        {prop.status === 'PROPOSED' && (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleApplySandwichProposal(prop.id)}
                            sx={{ borderRadius: '8px', textTransform: 'none', bgcolor: ACCENT }}
                          >
                            Apply
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#6b7280' }}>
                      No open sandwich proposals.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* SUB-TAB 4: Unexcused Absence Reviews */}
      {subTab === 4 && (
        <>
          <Paper elevation={0} sx={{ ...CARD_SX, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827' }}>
                  Unexcused Absence Candidate Review (TASK-LEV-007)
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  Identifies working days without punches or approved leave for human HR determination. Zero automated payroll deduction or attendance mutation.
                </Typography>
              </Box>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  size="small"
                  label="Target Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={absenceTargetDate}
                  onChange={(e) => setAbsenceTargetDate(e.target.value)}
                />
                <Button
                  variant="contained"
                  disabled={scanningAbsences}
                  onClick={handleScanAbsences}
                  sx={{ bgcolor: '#d97706', borderRadius: '12px', fontWeight: 700, textTransform: 'none' }}
                >
                  {scanningAbsences ? 'Scanning...' : 'Scan Absence Candidates'}
                </Button>
              </Stack>
            </Box>
          </Paper>

          <TableContainer component={Paper} sx={{ ...TABLE_PAPER_SX, borderRadius: '22px', border: '1px solid #eef0f4' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={HEAD_CELL_SX}>CANDIDATE ID</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>DATE</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>EMPLOYEE</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>EVIDENCE</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>STATUS</TableCell>
                  <TableCell sx={{ ...HEAD_CELL_SX, textAlign: 'center' }}>HR DETERMINATION</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingAbsences ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                ) : absenceCandidates.length > 0 ? (
                  absenceCandidates.map((c) => (
                    <TableRow key={c.id} sx={TABLE_ROW_SX}>
                      <TableCell sx={BODY_CELL_SX}>#{c.id}</TableCell>
                      <TableCell sx={BODY_CELL_SX}>{formatDate(c.absence_date)}</TableCell>
                      <TableCell sx={BODY_CELL_SX}>{c.employee_name || c.employee}</TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Shift: {c.evidence?.shift || 'Regular'} | Punches: 0
                        </Typography>
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <Chip
                          size="small"
                          label={c.status}
                          sx={{
                            fontWeight: 700,
                            bgcolor: c.status === 'CANDIDATE' ? '#fef3c7' : '#f1f5f9',
                            color: c.status === 'CANDIDATE' ? '#b45309' : '#334155',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...BODY_CELL_SX, textAlign: 'center' }}>
                        {c.status === 'CANDIDATE' && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setSelectedAbsence(c);
                              setAbsenceReviewDialog(true);
                            }}
                            sx={{ borderRadius: '8px', textTransform: 'none' }}
                          >
                            Review
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#6b7280' }}>
                      No unexcused absence review candidates.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Dialog open={absenceReviewDialog} onClose={() => setAbsenceReviewDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
            <DialogTitle sx={{ fontWeight: 700 }}>Record Absence Review Determination</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Determination Decision</InputLabel>
                  <Select value={absenceDecision} label="Determination Decision" onChange={(e) => setAbsenceDecision(e.target.value)}>
                    <MenuItem value="EXCUSED">Excuse Absence (Valid Reason Provided)</MenuItem>
                    <MenuItem value="UNEXCUSED">Confirm Unexcused (Requires Manual HR Action)</MenuItem>
                    <MenuItem value="DISMISSED">Dismiss Candidate</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Review Notes / Rationale *"
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  value={absenceNotes}
                  onChange={(e) => setAbsenceNotes(e.target.value)}
                  placeholder="Record justification for audit compliance."
                />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setAbsenceReviewDialog(false)}>Cancel</Button>
              <Button onClick={handleRecordAbsenceDecision} variant="contained" sx={{ bgcolor: ACCENT, borderRadius: '10px' }}>
                Save Determination
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {/* SUB-TAB 5: Stale Escalations (Batch 2) */}
      {subTab === 5 && (
        <>
          <Paper elevation={0} sx={{ ...CARD_SX, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827' }}>
                  Unfunded Stale Leave Escalations (TASK-LEV-005)
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  Requests whose approval elapsed without manager response and which lack available leave balance. Escalated to HR with zero automated LOP deduction.
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={scanning ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                disabled={scanning}
                onClick={handleTriggerStaleScan}
                sx={{
                  bgcolor: '#d97706',
                  fontWeight: 700,
                  borderRadius: '12px',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#b45309' },
                }}
              >
                {scanning ? 'Scanning...' : 'Trigger Stale Scan'}
              </Button>
            </Box>
          </Paper>

          <TableContainer component={Paper} sx={{ ...TABLE_PAPER_SX, borderRadius: '22px', border: '1px solid #eef0f4' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={HEAD_CELL_SX}>REQUEST ID</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>EMPLOYEE</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>LEAVE TYPE</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>DAYS</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>STATUS</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>ESCALATION REASON</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingEscalations ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                ) : escalations.length > 0 ? (
                  escalations.map((req) => (
                    <TableRow key={req.id} sx={TABLE_ROW_SX}>
                      <TableCell sx={BODY_CELL_SX}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          #{req.id}
                        </Typography>
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                          {req.employee_name} ({req.employee_no})
                        </Typography>
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <Typography variant="body2" sx={{ color: '#374151' }}>
                          {req.leave_type}
                        </Typography>
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: ACCENT }}>
                          {req.leave_days}
                        </Typography>
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <Chip
                          size="small"
                          label="APPROVAL ESCALATED"
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            borderRadius: '8px',
                            bgcolor: '#fef3c7',
                            color: '#b45309',
                            border: '1px solid #fde68a',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <Typography variant="body2" sx={{ color: '#dc2626', fontWeight: 500 }}>
                          {req.status_reason || 'Unfunded stale leave escalated to HR'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#6b7280' }}>
                      No escalated leave requests requiring HR review.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
